import { Box, Button, Grid, Switch, Typography } from "@mui/material";
import { colors, alphaVar } from "../../../../theme/muiColors";
import { IPaypalMerchantInfo } from "../../interfaces";
import * as _ from "lodash";
import { useGetMerchantInfo } from "../../actions/useGetMerchantInfo";
import { useDialog } from "../../../ui/dialogs/useDialog";
import { LinkOffOutlined } from "@mui/icons-material";
import { useDeleteMerchantInfo } from "../../hooks/useDeleteMerchantInfo";
import { useUpdatePayLaterEnabled } from "../../hooks/useUpdatePayLaterEnabled";
import { missingScopes } from "../../utils";
import CableIcon from '@mui/icons-material/Cable';
import { SlotField } from "./components/SlotField";
import { Title } from "./components/Title";
import { PaymentsReceivableField } from "./components/PaymentsReceivableField";
import { Scopes } from "./components/Scopes";
import { orange, red } from "@mui/material/colors";
import { grey } from "../../../../theme/muiColors";

export interface IPaypalMerchantInfoProps {
  merchantInfo?: IPaypalMerchantInfo | null;
  merchantId?: string;
  onDisconnected?: () => void;
}



export const PaypalMerchantInfo = ({ merchantInfo, onDisconnected }: IPaypalMerchantInfoProps) => {
  const { data: merchantInfoData, error } = useGetMerchantInfo(!_.isEmpty(merchantInfo)); //hydrate component if no passed merchantInfo obj
  const merchantId = merchantInfo?.merchantId ?? merchantInfoData?.merchantId;
  const paymentsReceivable = merchantInfo?.paymentsReceivable ?? merchantInfoData?.paymentsReceivable;
  const primaryEmailConfirmed = merchantInfo?.primaryEmailConfirmed ?? merchantInfoData?.primaryEmailConfirmed;
  const primaryEmail = merchantInfo?.primaryEmail ?? merchantInfoData?.primaryEmail;
  const scopes = merchantInfo?.scopes ?? merchantInfoData?.scopes;
  const cryptoPaymentsEnabled = merchantInfo?.cryptoPaymentsEnabled ?? merchantInfoData?.cryptoPaymentsEnabled; //TODO: Work with this!
  const payLaterEnabled = merchantInfo?.payLaterEnabled ?? merchantInfoData?.payLaterEnabled ?? false;
  const { mutate: deleteMerchantInfo } = useDeleteMerchantInfo();
  const { mutate: updatePayLaterEnabled, isPending: isUpdatingPayLater } = useUpdatePayLaterEnabled();
  const { showDialog, DialogComponent } = useDialog();
  const missingScopesList = missingScopes(scopes);
  if (!merchantInfo) return null;


  const handleDisconnect = () => {
    showDialog({
      type: "confirm",
      title: "Disconnect PayPal account",
      message: "Disconnecting your PayPal account will prevent you from offering PayPal services and products on yourwebsite. Do you wish to continue",
      onConfirm: () => deleteMerchantInfo(undefined, { onSuccess: onDisconnected }),
      confirmLabel: "Disconnect",
      cancelLabel: "Cancel",
      onCancel: () => { },
    });
  };

  return (
    <Box sx={{ p: 2, borderRadius: 2, borderColor: alphaVar(colors.grey[400], 0.5), borderWidth: 1, borderStyle: "solid" }}>

      <Title title="Connection Details" />

      <Grid container spacing={2} sx={{ width: "100%" }}>
        <Grid item xs={6}>
          <SlotField label="Merchant ID" value={merchantId} />

        </Grid>
        <Grid item xs={6}>
          <SlotField label="Primary Email" value={primaryEmail} />
          {
            primaryEmailConfirmed && (
              <Typography variant="caption" sx={{ color: "warning.main", mt: 1, fontStyle: "italic", textAlign: "center" }}>{"Attention: Please confirm your email address on https://www.paypal.com/businessprofile/settings in order to receive payments! You currently cannot receive payments."}</Typography>
            )
          }
        </Grid>
      </Grid>

      <Typography variant="body1" sx={{ fontWeight: 500, color: grey[800], mb: 1 }}>PERMISSIONS STATUS</Typography>
      <PaymentsReceivableField value={paymentsReceivable} />


      <Scopes scopes={scopes} />

      <Typography variant="body1" sx={{ fontWeight: 500, color: grey[800], mb: 1, mt: 2 }}>PAY LATER</Typography>
      <Typography variant="body1" sx={{ fontWeight: 400, color: grey[600], mb: 1, mt: 0 }}>Pay later is a feature that allows you to delay the payment of your orders until the customer has received the goods or services.</Typography>
      <Switch
        checked={payLaterEnabled}
        disabled={isUpdatingPayLater}
        sx={{
          "& .MuiSwitch-thumb": {
            backgroundColor: orange[400],
          },
          "& .MuiSwitch-track": {
            backgroundColor: alphaVar(orange[400], 0.8),
          },
          "& .MuiSwitch-switchBase.Mui-checked": {
            color: orange[400],
            "&:hover": { backgroundColor: `${orange[400]}14` },
          },
          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
            backgroundColor: orange[400],
          },
        }}
        onChange={(_, checked) => updatePayLaterEnabled(checked)}
      />

      <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          size="medium"
          color="inherit"
          startIcon={<LinkOffOutlined />}
          onClick={handleDisconnect}
          sx={{
            borderColor: alphaVar(orange[200], 0.8),
            borderRadius: 2,
            color: orange[500],
            textTransform: "none",
            "&:hover": { borderColor: alphaVar(orange[200], 0.8), backgroundColor: alphaVar(orange[50], 0.8) },
          }}
        >
          Disconnect
        </Button>
      </Box>
      {DialogComponent}
    </Box>
  );
};